import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import { CalendarCheck, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';
import api from '../../utils/api';

function formatLocalYmd(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toTimeString(dateLike) {
  const d = new Date(dateLike);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function combineBookingDayWithTime(bookingDate, hhmm) {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  const day = new Date(bookingDate);
  day.setHours(h, m, 0, 0);
  return day;
}

function SupplierBookingPendingActions({ booking, onBookingUpdated }) {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const rescheduleBookingIdRef = useRef(null);

  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [approveAllowed, setApproveAllowed] = useState([]);
  const [approveHasRestriction, setApproveHasRestriction] = useState(false);
  const [approveSelectedId, setApproveSelectedId] = useState('');
  const [approveShiftStart, setApproveShiftStart] = useState('');
  const [approveShiftEnd, setApproveShiftEnd] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentSubmitError, setAdjustmentSubmitError] = useState('');
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [rescheduleStaffMemberId, setRescheduleStaffMemberId] = useState('');
  const [rescheduleSelectedDate, setRescheduleSelectedDate] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false);
  const [rescheduleStaffMembers, setRescheduleStaffMembers] = useState([]);

  const [crmAfterApproveOpen, setCrmAfterApproveOpen] = useState(false);
  const [crmAfterApproveContext, setCrmAfterApproveContext] = useState(null);
  const [crmAfterApproveSubmitting, setCrmAfterApproveSubmitting] = useState(false);
  const [crmAfterApproveError, setCrmAfterApproveError] = useState('');

  const isMissed = booking ? new Date(booking.startTime) < new Date() : false;
  const businessId =
    selectedBusiness?.business?._id ||
    booking?.business?._id ||
    booking?.business;

  const closeApproveDialog = () => {
    setApproveDialogOpen(false);
    setApproveError('');
    setApproveAllowed([]);
    setApproveHasRestriction(false);
    setApproveSelectedId('');
    setApproveSubmitting(false);
  };

  const closeAdjustmentModal = () => {
    setAdjustmentModalOpen(false);
    setRescheduleStaffMemberId('');
    setRescheduleSelectedDate(null);
    setRescheduleSlots([]);
    rescheduleBookingIdRef.current = null;
    setAdjustmentReason('');
    setNewStartTime('');
    setNewEndTime('');
    setAdjustmentSubmitError('');
  };

  const refreshBooking = async () => {
    if (!booking?._id) return;
    const response = await api.get(`/api/unified-bookings/${booking._id}`);
    onBookingUpdated?.(response.data);
  };

  const startApproveDialog = async () => {
    if (!booking) return;
    setApproveError('');
    setApproveLoading(true);
    setApproveDialogOpen(true);
    setApproveShiftStart(toTimeString(booking.startTime));
    setApproveShiftEnd(toTimeString(booking.endTime));
    try {
      const res = await api.get(`/api/unified-bookings/${booking._id}/allowed-team-members`);
      const list = res.data?.allowedTeamMembers || [];
      const hasRestriction = !!res.data?.hasRestriction;
      setApproveAllowed(list);
      setApproveHasRestriction(hasRestriction);

      const preAssignedId =
        booking.assignedTeamMembers?.[0]?.member?._id ||
        booking.assignedTeamMembers?.[0]?.member;
      if (preAssignedId && list.some((m) => m.id?.toString() === preAssignedId.toString())) {
        setApproveSelectedId(preAssignedId.toString());
      } else if (list.length === 1) {
        setApproveSelectedId(list[0].id.toString());
      } else {
        setApproveSelectedId('');
      }
    } catch (err) {
      setApproveError(err.response?.data?.message || 'Failed to load allowed team members.');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!booking || !approveSelectedId) return;
    const selectedMember = approveAllowed.find((m) => m.id?.toString() === approveSelectedId);
    if (!selectedMember) return;
    setApproveError('');
    setApproveSubmitting(true);
    try {
      if (!selectedMember.hasShiftCoveringBooking) {
        if (!approveShiftStart || !approveShiftEnd) {
          setApproveError('Please provide a shift start and end time.');
          setApproveSubmitting(false);
          return;
        }
        const shiftStart = combineBookingDayWithTime(booking.startTime, approveShiftStart);
        const shiftEnd = combineBookingDayWithTime(booking.startTime, approveShiftEnd);
        if (shiftStart >= shiftEnd) {
          setApproveError('Shift end time must be after the start time.');
          setApproveSubmitting(false);
          return;
        }
        if (shiftStart > new Date(booking.startTime) || shiftEnd < new Date(booking.endTime)) {
          setApproveError('The shift must cover the full appointment time.');
          setApproveSubmitting(false);
          return;
        }
        await api.post('/api/team-member-shifts', {
          teamMemberId: approveSelectedId,
          startTime: shiftStart.toISOString(),
          endTime: shiftEnd.toISOString(),
        });
      }

      const res = await api.put(`/api/unified-bookings/${booking._id}/status`, {
        status: 'confirmed',
        notifyClient: true,
        assignedTeamMembers: [approveSelectedId],
      });

      const suggest = Boolean(res.data?.suggestAddToCrm);
      const ctx =
        suggest && res.data
          ? {
              bookingId: res.data._id || booking._id,
              name: res.data.manualBookingDetails?.clientName || '',
              email: res.data.manualBookingDetails?.clientEmail || '',
              phone: res.data.manualBookingDetails?.clientPhone || '',
            }
          : null;

      closeApproveDialog();
      await refreshBooking();

      if (suggest && ctx) {
        setCrmAfterApproveContext(ctx);
        setCrmAfterApproveError('');
        setCrmAfterApproveOpen(true);
      }
    } catch (err) {
      setApproveError(err.response?.data?.message || 'Failed to approve booking.');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleAdjustClick = () => {
    setAdjustmentSubmitError('');
    setAdjustmentModalOpen(true);
    setRescheduleStaffMemberId('');
    setRescheduleSelectedDate(null);
    setRescheduleSlots([]);
    setNewStartTime('');
    setNewEndTime('');
    setAdjustmentReason('');
    rescheduleBookingIdRef.current = booking._id;
    if (businessId) {
      api
        .get(`/api/supplier/staff-members?businessId=${businessId}`)
        .then((res) => setRescheduleStaffMembers(res.data.staffMembers || []))
        .catch(() => setRescheduleStaffMembers([]));
    } else {
      setRescheduleStaffMembers([]);
    }
  };

  const fetchRescheduleSlots = useCallback(async () => {
    if (!rescheduleStaffMemberId || !rescheduleSelectedDate || !booking) return;
    if (!businessId) return;
    setLoadingRescheduleSlots(true);
    try {
      const duration = booking.serviceType?.duration || 60;
      const dateParam = formatLocalYmd(rescheduleSelectedDate);
      const openRes = await api.get(`/api/opening-hours/${businessId}/availability/${dateParam}`, {
        params: { duration },
      });
      const openingSlots = openRes.data?.availableSlots || [];
      const now = new Date();
      const slots = openingSlots
        .map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
        }))
        .filter((slot) => new Date(slot.startTime) > now);
      setRescheduleSlots(slots);
    } catch {
      setRescheduleSlots([]);
    } finally {
      setLoadingRescheduleSlots(false);
    }
  }, [rescheduleStaffMemberId, rescheduleSelectedDate, booking, businessId]);

  useEffect(() => {
    if (rescheduleStaffMemberId && rescheduleSelectedDate && booking) {
      fetchRescheduleSlots();
    } else {
      setRescheduleSlots([]);
    }
  }, [rescheduleStaffMemberId, rescheduleSelectedDate, booking, fetchRescheduleSlots]);

  const handleAdjustmentSubmit = async () => {
    const bookingId = rescheduleBookingIdRef.current || booking._id;
    if (!bookingId) return;
    setAdjustmentSubmitError('');
    setAdjustmentSubmitting(true);
    try {
      await api.put(`/api/unified-bookings/${bookingId}/status`, {
        status: 'rescheduled',
        newStartTime,
        newEndTime,
        notes: adjustmentReason,
        notifyClient: true,
        ...(rescheduleStaffMemberId && {
          assignedTeamMembers: [rescheduleStaffMemberId],
          forceAssign: true,
        }),
      });
      closeAdjustmentModal();
      await refreshBooking();
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || 'Failed to send reschedule request';
      setAdjustmentSubmitError(msg);
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!booking?._id) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.put(`/api/unified-bookings/${booking._id}/status`, {
        status: 'rejected',
        notifyClient: true,
      });
      setDeclineDialogOpen(false);
      await refreshBooking();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Could not decline this booking.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleAddToCouplesAfterApprove = async () => {
    if (!crmAfterApproveContext?.bookingId) return;
    setCrmAfterApproveSubmitting(true);
    setCrmAfterApproveError('');
    try {
      const res = await api.post(`/api/unified-bookings/${crmAfterApproveContext.bookingId}/add-to-couples`, {
        name: crmAfterApproveContext.name,
        email: crmAfterApproveContext.email,
        phone: crmAfterApproveContext.phone,
      });
      setCrmAfterApproveOpen(false);
      setCrmAfterApproveContext(null);
      const leadId = res.data?.lead?._id;
      if (leadId) {
        navigate(`/supplier/crm/leads/${leadId}`);
      }
    } catch (err) {
      setCrmAfterApproveError(err.response?.data?.message || 'Could not add this couple.');
    } finally {
      setCrmAfterApproveSubmitting(false);
    }
  };

  if (!booking || booking.status !== 'pending') {
    return null;
  }

  return (
    <>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      ) : null}

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Your response
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {!isMissed && (
          <Button variant="contained" color="success" onClick={() => void startApproveDialog()}>
            Approve
          </Button>
        )}
        <Button variant="outlined" color="info" onClick={handleAdjustClick}>
          Suggest alternative date
        </Button>
        <Button variant="outlined" color="error" onClick={() => setDeclineDialogOpen(true)}>
          Decline
        </Button>
      </Stack>

      <Dialog open={declineDialogOpen} onClose={() => !actionBusy && setDeclineDialogOpen(false)}>
        <DialogTitle>Decline this booking?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The couple will be notified that this appointment request was declined.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)} disabled={actionBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeclineConfirm} disabled={actionBusy}>
            {actionBusy ? 'Declining…' : 'Decline booking'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveDialogOpen}
        onClose={approveSubmitting ? undefined : closeApproveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve appointment</DialogTitle>
        <DialogContent>
          {approveLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {approveError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {approveError}
                </Alert>
              ) : null}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {approveHasRestriction
                  ? 'Only team members permitted for this appointment type are shown.'
                  : 'All active team members are available for this appointment type.'}
              </Typography>

              {approveAllowed.length === 0 ? (
                <Alert severity="warning">
                  No team members are permitted for this appointment type. Update the appointment type&apos;s allowed team members in Booking Management.
                </Alert>
              ) : (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Team member</InputLabel>
                  <Select
                    label="Team member"
                    value={approveSelectedId}
                    onChange={(e) => setApproveSelectedId(e.target.value)}
                  >
                    {approveAllowed.map((m) => (
                      <MenuItem key={m.id} value={m.id.toString()}>
                        {m.name}
                        {!m.hasShiftCoveringBooking && (
                          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            (needs shift)
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {approveSelectedId &&
                (() => {
                  const member = approveAllowed.find((m) => m.id?.toString() === approveSelectedId);
                  if (!member) return null;
                  if (member.hasShiftCoveringBooking) {
                    return (
                      <Alert severity="success">
                        {member.name} is scheduled to cover this appointment.
                      </Alert>
                    );
                  }
                  return (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {member.name} doesn&apos;t have a shift this day. Add their shift below, or suggest an alternative date instead.
                      </Alert>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          label="Shift start"
                          type="time"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={approveShiftStart}
                          onChange={(e) => setApproveShiftStart(e.target.value)}
                        />
                        <TextField
                          label="Shift end"
                          type="time"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={approveShiftEnd}
                          onChange={(e) => setApproveShiftEnd(e.target.value)}
                        />
                      </Stack>
                    </Box>
                  );
                })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveDialog} disabled={approveSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSubmit}
            disabled={approveLoading || approveSubmitting || !approveSelectedId || approveAllowed.length === 0}
          >
            {approveSubmitting ? 'Approving…' : 'Approve booking'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adjustmentModalOpen} onClose={adjustmentSubmitting ? undefined : closeAdjustmentModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarCheck size={24} />
              <Typography variant="h6">Suggest alternative date</Typography>
            </Box>
            <IconButton onClick={closeAdjustmentModal} size="small" sx={{ color: 'text.secondary' }}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Staff member
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={rescheduleStaffMemberId}
                    onChange={(e) => {
                      setRescheduleStaffMemberId(e.target.value);
                      setNewStartTime('');
                      setNewEndTime('');
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">Select staff member</MenuItem>
                    {rescheduleStaffMembers.map((sm) => {
                      const name = sm.user
                        ? `${sm.user.firstName || ''} ${sm.user.lastName || ''}`.trim() || sm.name
                        : sm.name || 'Staff';
                      return (
                        <MenuItem key={sm._id} value={sm._id}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    value={rescheduleSelectedDate}
                    onChange={(d) => {
                      setRescheduleSelectedDate(d);
                      setNewStartTime('');
                      setNewEndTime('');
                    }}
                    minDate={new Date()}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Available time slots
                </Typography>
                {loadingRescheduleSlots ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading slots…
                    </Typography>
                  </Box>
                ) : rescheduleSlots.length === 0 && rescheduleStaffMemberId && rescheduleSelectedDate ? (
                  <Typography variant="body2" color="text.secondary">
                    No available slots on this date. The business may be closed that day or there are no times within opening hours for this appointment length.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {rescheduleSlots.map((slot) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      const label = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      const isSelected = newStartTime === slot.startTime;
                      return (
                        <Button
                          key={slot.startTime}
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                          size="small"
                          onClick={() => {
                            setNewStartTime(slot.startTime);
                            setNewEndTime(slot.endTime);
                          }}
                          sx={{ flexDirection: 'column', alignItems: 'stretch', py: 0.75 }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Reason for rescheduling
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="Please explain why you are suggesting a different time…"
                  variant="outlined"
                />
              </Grid>
              {adjustmentSubmitError ? (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => setAdjustmentSubmitError('')}>
                    {adjustmentSubmitError}
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    The couple will be notified by email and in the app. They will need to confirm the new time.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={closeAdjustmentModal} disabled={adjustmentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleAdjustmentSubmit}
            disabled={!newStartTime || !adjustmentReason || adjustmentSubmitting}
          >
            {adjustmentSubmitting ? 'Sending…' : 'Send suggestion'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={crmAfterApproveOpen} onClose={() => setCrmAfterApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add this couple to My Couples?</DialogTitle>
        <DialogContent>
          {crmAfterApproveError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {crmAfterApproveError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            This appointment is not linked to anyone in your pipeline yet. Adding a couple keeps notes, follow-ups, and future bookings in one place.
          </Typography>
          {crmAfterApproveContext?.name ? (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Name:</strong> {crmAfterApproveContext.name}
            </Typography>
          ) : null}
          {crmAfterApproveContext?.email ? (
            <Typography variant="body2">
              <strong>Email:</strong> {crmAfterApproveContext.email}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrmAfterApproveOpen(false)} disabled={crmAfterApproveSubmitting}>
            Not now
          </Button>
          <Button variant="contained" onClick={handleAddToCouplesAfterApprove} disabled={crmAfterApproveSubmitting}>
            {crmAfterApproveSubmitting ? 'Adding…' : 'Add to couples'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SupplierBookingPendingActions;
